import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useApi } from '@/lib/api/useApi';
import { US_STATES } from '@/lib/usStates';

/**
 * View scope — a client-side lens over the stores the signed-in user is
 * authorized to see. Admins (and anyone scoped to several stores) can narrow
 * the UI to a single store or a state without changing server-side
 * authorization: every API call still runs against the principal's full
 * scope; screens filter what they display and pre-select stores in forms.
 */

export interface ScopeStore {
  id: string;
  code: string;
  name: string;
  /** Two-letter US state code — the region grouping for the scope picker. */
  state: string | null;
  isKiosk: boolean;
}

export type ViewScopeSelection =
  | { kind: 'all' }
  | { kind: 'state'; state: string }
  | { kind: 'store'; storeId: string };

export interface ViewScopeValue {
  /** Current selection; defaults to all stores. */
  selection: ViewScopeSelection;
  setSelection: (s: ViewScopeSelection) => void;
  /** All stores the user can see (from /my/stores), [] while loading. */
  stores: ScopeStore[];
  /** Distinct 2-letter state codes across visible stores, sorted; [] if none set. */
  states: string[];
  /** Stores inside the current selection. */
  activeStores: ScopeStore[];
  /** True when the given store id is inside the current selection. */
  inScope: (storeId: string) => boolean;
  /** "CODE — Name" for a visible store id, or the id itself while loading. */
  storeLabel: (storeId: string) => string;
  /** Short human label for the current selection, e.g. "TPA — Tampa". */
  label: string;
}

function stateName(code: string): string {
  return US_STATES.find((s) => s.code === code)?.name ?? code;
}

const ALL: ViewScopeSelection = { kind: 'all' };
const STORAGE_KEY = 'qargo.view-scope';

/**
 * Default used outside the provider (kiosk screens, unit tests): everything
 * in scope, selection pinned to "all".
 */
const FALLBACK: ViewScopeValue = {
  selection: ALL,
  setSelection: () => undefined,
  stores: [],
  states: [],
  activeStores: [],
  inScope: () => true,
  storeLabel: (storeId) => storeId,
  label: 'All stores',
};

const ViewScopeContext = createContext<ViewScopeValue>(FALLBACK);

function loadPersisted(): ViewScopeSelection {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return ALL;
    const parsed = JSON.parse(raw) as ViewScopeSelection;
    if (parsed.kind === 'state' && typeof parsed.state === 'string') return parsed;
    if (parsed.kind === 'store' && typeof parsed.storeId === 'string') return parsed;
    return ALL;
  } catch {
    return ALL;
  }
}

export function ViewScopeProvider({ children }: { children: ReactNode }) {
  const { data } = useApi<{ stores: ScopeStore[] }>('/my/stores');
  const stores = useMemo(() => data?.stores ?? [], [data]);
  const [selection, setSelectionState] = useState<ViewScopeSelection>(loadPersisted);

  const setSelection = useCallback((s: ViewScopeSelection) => {
    setSelectionState(s);
    try {
      if (s.kind === 'all') window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch {
      // Persistence is best-effort; private mode etc. just loses the pick.
    }
  }, []);

  // Drop a persisted selection that no longer matches the user's scope
  // (store removed, state changed, different account on this browser).
  useEffect(() => {
    if (stores.length === 0) return;
    if (selection.kind === 'store' && !stores.some((s) => s.id === selection.storeId)) {
      setSelection(ALL);
    }
    if (selection.kind === 'state' && !stores.some((s) => s.state === selection.state)) {
      setSelection(ALL);
    }
  }, [stores, selection, setSelection]);

  const value = useMemo<ViewScopeValue>(() => {
    const states = Array.from(
      new Set(stores.map((s) => s.state).filter((r): r is string => !!r)),
    ).sort();
    const activeStores =
      selection.kind === 'all'
        ? stores
        : selection.kind === 'state'
          ? stores.filter((s) => s.state === selection.state)
          : stores.filter((s) => s.id === selection.storeId);
    // Until /my/stores resolves we can't validate membership — show everything
    // rather than flashing empty screens.
    const activeIds = new Set(activeStores.map((s) => s.id));
    const inScope = (storeId: string) =>
      stores.length === 0 || selection.kind === 'all' ? true : activeIds.has(storeId);
    const storeLabel = (storeId: string) => {
      const s = stores.find((x) => x.id === storeId);
      return s ? `${s.code} — ${s.name}` : storeId;
    };
    const label =
      selection.kind === 'all'
        ? 'All stores'
        : selection.kind === 'state'
          ? stateName(selection.state)
          : (() => {
              const s = stores.find((x) => x.id === selection.storeId);
              return s ? `${s.code} — ${s.name}` : 'One store';
            })();
    return { selection, setSelection, stores, states, activeStores, inScope, storeLabel, label };
  }, [stores, selection, setSelection]);

  return <ViewScopeContext.Provider value={value}>{children}</ViewScopeContext.Provider>;
}

export function useViewScope(): ViewScopeValue {
  return useContext(ViewScopeContext);
}

/**
 * Header dropdown for switching the view scope. Hidden for single-store users
 * — there is nothing to switch.
 */
export function ScopePicker() {
  const { selection, setSelection, stores, states } = useViewScope();
  if (stores.length <= 1) return null;

  const encoded =
    selection.kind === 'all'
      ? 'all'
      : selection.kind === 'state'
        ? `state:${selection.state}`
        : `store:${selection.storeId}`;

  function onChange(v: string) {
    if (v === 'all') setSelection({ kind: 'all' });
    else if (v.startsWith('state:')) setSelection({ kind: 'state', state: v.slice(6) });
    else setSelection({ kind: 'store', storeId: v.slice(6) });
  }

  return (
    <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className="hidden md:inline">Viewing</span>
      <select
        aria-label="Viewing scope"
        value={encoded}
        onChange={(e) => onChange(e.target.value)}
        className="max-w-[180px] rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground sm:max-w-[240px]"
      >
        <option value="all">All stores ({stores.length})</option>
        {states.length > 0 ? (
          <optgroup label="States">
            {states.map((st) => (
              <option key={st} value={`state:${st}`}>
                {stateName(st)} ({stores.filter((s) => s.state === st).length})
              </option>
            ))}
          </optgroup>
        ) : null}
        <optgroup label="Stores">
          {stores.map((s) => (
            <option key={s.id} value={`store:${s.id}`}>
              {s.code} — {s.name}
            </option>
          ))}
        </optgroup>
      </select>
    </label>
  );
}
